import { Children, cloneElement, isValidElement, useContext } from 'react'
import type { ComponentProps, ReactElement } from 'react'
import { __RouterContext, matchPath } from 'react-router'
import type { match, RouteProps } from 'react-router'
import type { Switch } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { useLocaleStrippedLocation } from './useLocaleStrippedLocation'

/**
 * Matches existing `/:regionCode/...` routes when an optional locale segment is
 * present after the region, e.g. `/au/de-DE/shop` matches `/au/shop`.
 * Descendants still receive the original location so localized URLs are not
 * collapsed when components rebuild navigation targets from `useLocation()`.
 * This is a hacky way, we shouldn't be importing framework internals directly like this
 */
type LocaleAwareSwitchChildProps = RouteProps & {
  computedMatch?: match
  from?: RouteProps['path']
}

export default function LocaleAwareSwitch(props: ComponentProps<typeof Switch>) {
  const routerContext = useContext(__RouterContext)
  const location = useLocation()
  const matchLocation = useLocaleStrippedLocation()
  let element: ReactElement<LocaleAwareSwitchChildProps> | undefined
  let computedMatch: match | null = null

  Children.forEach(props.children, child => {
    if (computedMatch || !isValidElement<LocaleAwareSwitchChildProps>(child)) return

    element = child
    const path = child.props.path || child.props.from
    computedMatch = path ? matchPath(matchLocation.pathname, { ...child.props, path }) : routerContext.match
  })

  return computedMatch && element ? cloneElement(element, { location, computedMatch }) : null
}
